/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { ref, watch } from 'vue'
import { Plus, Users, Scale, Weight, Eye, Thermometer, StickyNote } from 'lucide-vue-next'

const STORAGE_KEY = 'digiziege_quick_actions'

/**
 * Definition of all available Quick Actions.
 * Each action has an ID, translation key for the label, icon component, and router destination.
 */
export const AVAILABLE_ACTIONS = [
  { id: 'new_goat', labelKey: 'views.home.activity.new_goat', icon: Plus, route: { name: 'NewGoat' } },
  { id: 'new_group', labelKey: 'views.home.activity.new_group', icon: Users, route: { name: 'NewGroup' } },
  { id: 'session_weight', labelKey: 'views.session.options.weight', icon: Weight, route: { name: 'SessionGroupSelect', params: { type: 'weight' } } },
  { id: 'session_famacha', labelKey: 'views.session.options.famacha', icon: Eye, route: { name: 'SessionGroupSelect', params: { type: 'famacha' } } },
  { id: 'session_temperature', labelKey: 'views.session.options.temperature', icon: Thermometer, route: { name: 'SessionGroupSelect', params: { type: 'temperature' } } },
  { id: 'session_note', labelKey: 'views.session.options.note', icon: StickyNote, route: { name: 'SessionGroupSelect', params: { type: 'note' } } },
  { id: 'licenses', labelKey: 'views.more.licenses.title', icon: Scale, route: { name: 'Licenses' } },
]

// default values for Quick Links
const DEFAULT_IDS = ['new_goat', 'session_weight', 'licenses']

/**
 * Initialize state from localStorage or fall back to defaults.
 * Defined outside the function to create a shared state across components.
 */

const stored = localStorage.getItem(STORAGE_KEY)
const initialIds = stored ? JSON.parse(stored) : DEFAULT_IDS
const selectedActionIds = ref(initialIds)

/**
 * Watch for changes in selected actions and persist to localStorage.
 * This ensures user preferences are saved immediately.
 */
watch(selectedActionIds, (newVal) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(newVal))
}, { deep: true })

/**
 * Composable for managing Quick Actions on the Home Dashboard.
 * Returns the shared state and definitions.
 * 
 * return an object containing:
 * - AVAILABLE_ACTIONS: Array of all possible action definitions
 * - selectedActionIds: Reactive array of selected action IDs
 */
export function useQuickActions() {
  return {
    AVAILABLE_ACTIONS,
    selectedActionIds
  }
}